import { Box } from "@mui/material";
import React from "react";
import Flex from "@/components/Flex";
import { NoDataAvailable } from "@/components/NoDataAvailable";
import { CHART_COLORS } from "@/components/app/provider/patient/chart";
import { TENANT_FEATURE } from "@/constants/tenant-feature-constant";
import useDisclosure from "@/hooks/use-disclosure";
import { useTenantFeatures } from "@/hooks/use-tenant-features";
import LegacyEncounterSummaryDrawer from "@/pages/app/provider/billings/encounter/LegacyEncounterSummaryDrawer";
import { PatientTimelineEvent } from "@/pages/app/provider/patient/patient-list/patient-chart/chronological-records/use-chronological-record";
import { SignedNoteSummary } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-preview";
import ChronologyEventRow from "./ChronologyEventRow";
import DateStrip from "./DateStrip";

interface PatientChronologyProps {
  chronologicalRecord?: Record<string, PatientTimelineEvent[]>;
  isLoadingData?: boolean;
  /** True once every available record has been loaded — shows the end-of-list note. */
  isEndOfList?: boolean;
}

function PatientChronology({
  chronologicalRecord,
  isLoadingData,
  isEndOfList,
}: Readonly<PatientChronologyProps>) {
  const encounterSummaryDisclosure = useDisclosure();
  const { isEnabled } = useTenantFeatures();
  const isPremierMdNote = isEnabled(TENANT_FEATURE.PREMIER_MD_F2F_NOTES);

  const handleAction = (event: PatientTimelineEvent) => {
    encounterSummaryDisclosure.open(event.eventReferenceId);
  };

  const ActionNameMapper: Record<string, string> = {
    ENCOUNTER_COMPLETED: "View",
  };

  // const handleMenuAction = (label: string, subOption?: string) => {
  //   console.log("Menu action:", label, subOption);
  // };

  // const _CHRONOLOGY_MENU_OPTIONS: MenuOption[] = [
  //   { label: "Amend Note", onClick: () => handleMenuAction("Amend Note") },
  //   {
  //     label: "Add or Edit Tags",
  //     onClick: () => handleMenuAction("Add or Edit Tags"),
  //   },
  //   {
  //     label: "Mark Confidential",
  //     onClick: () => handleMenuAction("Mark Confidential"),
  //   },
  //   {
  //     label: "Send",
  //     hasSubmenu: true,
  //     subOptions: [
  //       {
  //         label: "Office Message",
  //         onClick: () => handleMenuAction("Send", "Office Message"),
  //       },
  //       {
  //         label: "Provider letter",
  //         onClick: () => handleMenuAction("Send", "Provider letter"),
  //       },
  //       {
  //         label: "Provider Referral",
  //         onClick: () => handleMenuAction("Send", "Provider Referral"),
  //       },
  //       {
  //         label: "Patient Letter",
  //         onClick: () => handleMenuAction("Send", "Patient Letter"),
  //       },
  //     ],
  //   },
  //   {
  //     label: "Export to",
  //     hasSubmenu: true,
  //     subOptions: [
  //       {
  //         label: "New Note",
  //         onClick: () => handleMenuAction("Export to", "New Note"),
  //       },
  //       {
  //         label: "New Note (HPI Only)",
  //         onClick: () => handleMenuAction("Export to", "New Note (HPI Only)"),
  //       },
  //     ],
  //   },
  //   {
  //     label: "Print",
  //     hasSubmenu: true,
  //     subOptions: [
  //       { label: "Note", onClick: () => handleMenuAction("Print", "Note") },
  //       {
  //         label: "Visit Summary",
  //         onClick: () => handleMenuAction("Print", "Visit Summary"),
  //       },
  //       {
  //         label: "Patient Summary",
  //         onClick: () => handleMenuAction("Print", "Patient Summary"),
  //       },
  //       {
  //         label: "Simple Invoice (No Dx)",
  //         onClick: () => handleMenuAction("Print", "Simple Invoice (No Dx)"),
  //       },
  //       {
  //         label: "Claim Invoice with ICD-10",
  //         onClick: () => handleMenuAction("Print", "Claim Invoice with ICD-10"),
  //       },
  //       {
  //         label: "Claim Invoice with ICD-9",
  //         onClick: () => handleMenuAction("Print", "Claim Invoice with ICD-9"),
  //       },
  //     ],
  //   },
  //   { label: "Remove", onClick: () => handleMenuAction("Remove") },
  // ];

  let globalRowIndex = 0;

  if (Object.entries(chronologicalRecord ?? {}).length === 0)
    return (
      <Box height={"100%"}>
        <NoDataAvailable message="No Chronological Record Found" />
      </Box>
    );

  return (
    <>
      {isPremierMdNote ? (
        <SignedNoteSummary disclosure={encounterSummaryDisclosure} />
      ) : (
        <LegacyEncounterSummaryDrawer disclosure={encounterSummaryDisclosure} />
      )}

      <Flex flexDirection="column" sx={{ width: "100%", height: "100%" }}>
        {Object.entries(chronologicalRecord ?? {}).map(([date, eventData]) => (
          <React.Fragment key={date}>
            <DateStrip date={date} />
            {eventData.map((event) => {
              const rowBg =
                globalRowIndex % 2 === 0
                  ? CHART_COLORS.surfaceWhite
                  : CHART_COLORS.surfaceAlt;
              globalRowIndex++;
              return (
                <ChronologyEventRow
                  key={event.uuid || globalRowIndex}
                  event={event}
                  rowBg={rowBg}
                  actionLabel={ActionNameMapper[event.timelineEventType]}
                  onAction={handleAction}
                />
              );
            })}
          </React.Fragment>
        ))}

        {isLoadingData && (
          <div
            style={{
              minHeight: "30px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            loading...
          </div>
        )}

        {!isLoadingData && isEndOfList && (
          <div
            style={{
              minHeight: "30px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "12px",
              color: CHART_COLORS.textLabel,
            }}
          >
            No more data available
          </div>
        )}
      </Flex>
    </>
  );
}

export default PatientChronology;
